import { isEmpty, isNil } from 'lodash';
import { useContext } from 'react';
import { Button, ClearButton } from '@shared/components/Button';
import { Icon } from '@shared/components/Icon';
import { Image } from '@shared/components/Image';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { ASK_MARK_ICON } from '@shared/scenes/ask/components/AskMark';
import { WarningSubfooter } from '@shared/scenes/entity/components/WarningSubfooter';
import { useFieldConflictContext, useManagedComponents } from '@shared/scenes/entity/context';
import {
  FieldProvenance,
  useFieldProvenance,
  useFieldRevert,
} from '@shared/scenes/entity/provenance';
import {
  getComponentIconWithFallback,
  getComponentLabel,
  getComponentPlaceholder,
  isDisplayableComponent,
  isManagingInputComponent,
} from '@shared/scenes/workflows/utils';
import { ColorThemeContext, grid } from '@shared/styles';
import { getTestId } from '@shared/util/getTestId';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { EditFieldContainerProps } from './EditFieldContainerProps';
import classNames from './EditFieldContainer.module.css';

export function EditFieldContainer({
  component,
  children,
  icon = getComponentIconWithFallback(component),
  iconUrl,
  label = getComponentLabel(component),
  placeholder = getComponentPlaceholder(component),
  header,
  footer,
  subfooter,
  notes,
  files,
  showIcon = true,
  showLabel = true,
  showPlaceholder = false,
  showClearButton = false,
  error,
  helpText,
  onPress,
  onClear,
  testID = getTestId('card-field-cards', component.id),
  anchorName,
  largerInput = true,
  isGrayScale = false,
  isItalic = false,
  isReadOnly = false,
  style,
  printContent,
  placeholderColor,
  hasVisibleValue,
}: EditFieldContainerProps) {
  const colors = useContext(ColorThemeContext);
  const isDesktop = useIsDesktop();
  const conflictingComponentIds = useFieldConflictContext();
  const managedComponents = useManagedComponents(component);
  const fieldProvenance = useFieldProvenance(component.id);
  const revertField = useFieldRevert(component.id);
  // A mark against a field that is showing nothing claims a fill the user cannot see, so the mark
  // stands down — along with its revert control — rather than the field explaining itself.
  // `hasVisibleValue` is left undefined by every field that renders whatever it holds, which is why
  // only an explicit `false` suppresses.
  const provenance = hasVisibleValue === false ? undefined : fieldProvenance;
  const revert = hasVisibleValue === false ? undefined : revertField;
  const isConflicted = conflictingComponentIds.includes(component.id);
  const isMobile = !isDesktop;
  const isDisplayable = isDisplayableComponent(component);
  const shouldHaveIcon = showIcon && isDisplayable;
  const renderIconUrl = showIcon && shouldHaveIcon && !isNil(iconUrl);
  const renderIcon = showIcon && shouldHaveIcon && !isNil(icon) && !renderIconUrl;
  const renderLabel = showLabel && isDisplayable && !isNil(label);
  const renderPlaceholder = showPlaceholder && isDisplayable && !isNil(placeholder);
  const renderClearButton = showClearButton && !isNil(onClear);
  const hasError = !!error;
  // Managing components (e.g. DATE_RANGE) have no `required` of their own — it lives on the
  // managed children they own. Required when ANY child is, matching how the on-submit error is
  // derived in useManagedComponentRequiredErrors, so the hint and the error always agree.
  const isRequired = isManagingInputComponent(component)
    ? managedComponents.some(managed => 'required' in managed && managed.required === true)
    : 'required' in component && !!component.required;
  let helpContent: string | null = null;
  if (isRequired && helpText && !isReadOnly) {
    helpContent = i18n('card.form.fieldHelp.requiredWithHelp', { text: helpText });
  } else if (isRequired && !isReadOnly) {
    helpContent = i18n('card.form.fieldHelp.required');
  } else if (helpText) {
    helpContent = helpText;
  }

  const iconGridSize = isMobile && largerInput ? 4 : 3;
  const imageIconSize = isMobile && largerInput ? grid(3) : grid(2.5);

  return (
    <>
      <PrintContent label={label} content={printContent} />
      <View testID={testID} className={[classNames.container, 'no-print']} style={style}>
        {renderLabel && (
          <Text size="overline" weight="semibold" danger={hasError} className={classNames.label}>
            {label}
          </Text>
        )}
        <EditFieldWrapper
          isReadOnly={isReadOnly}
          hasError={hasError}
          isGrayScale={isGrayScale}
          isItalic={isItalic}
          onPress={onPress}
          label={label}
        >
          {header && <View className={classNames.header}>{header}</View>}
          <View className={[classNames.row, isMobile && largerInput && classNames.rowPadded]}>
            {renderIcon && (
              <View className={classNames.icon}>
                <Icon
                  name={icon}
                  gridSize={iconGridSize}
                  color={hasError ? colors.error.foreground : colors.accent}
                />
              </View>
            )}
            {renderIconUrl && (
              <View className={classNames.icon}>
                <Image
                  source={{ uri: iconUrl }}
                  style={{ width: imageIconSize, height: imageIconSize }}
                />
              </View>
            )}
            <View className={classNames.content}>
              {renderPlaceholder && (
                <View
                  className={classNames.placeholder}
                  style={placeholderColor ? { color: placeholderColor } : undefined}
                >
                  {placeholder}
                </View>
              )}
              {children}
            </View>
            {renderClearButton && (
              <View className={classNames.clearButton}>
                <ClearButton onClick={onClear} hasError={hasError} />
              </View>
            )}
          </View>
          {notes && <View className={classNames.notes}>{notes}</View>}
          {files && <View className={classNames.files}>{files}</View>}
          {footer && <View className={classNames.footer}>{footer}</View>}
          <View style={{ anchorName }} className={classNames.anchor} />
        </EditFieldWrapper>
        {isConflicted && (
          <View className={classNames.subfooter}>
            <WarningSubfooter
              showWarning
              message={i18n('card.update.remoteConflictFieldUpdated')}
            />
          </View>
        )}
        {subfooter && <View className={classNames.subfooter}>{subfooter}</View>}
        {hasError && (
          <View className={classNames.error}>
            <Text size="body2" danger>
              {error}
            </Text>
          </View>
        )}
        {!hasError && (provenance || helpContent) && (
          <View className={classNames.helpText}>
            {/* The mark leads the line rather than taking one of its own: where a value came from
                belongs with everything else this field has to say about itself, and a second line
                would push every field below it down the moment a Capture landed. */}
            {provenance && <ProvenanceMark provenance={provenance} onRevert={revert} />}
            {provenance && helpContent && (
              <Text size="body2" inactive className={classNames.helpSeparator}>
                {FIELD_HELP_SEPARATOR}
              </Text>
            )}
            {helpContent && (
              <Text size="body2" inactive>
                {helpContent}
              </Text>
            )}
          </View>
        )}
      </View>
    </>
  );
}

/**
 * The separator between the parts of the help line — the same bullet
 * `card.form.fieldHelp.requiredWithHelp` already joins `Required` to a field's own help text with,
 * so a line reads `Best guess • Required • …` whatever it happens to be carrying.
 */
const FIELD_HELP_SEPARATOR = ' \u2022 ';

/**
 * The caution ink a `needsReview` mark is written in — the same token the iOS app's review note
 * uses, drawn from the orange ramp behind every other warning rather than from the brand purple.
 */
const REVIEW_COLOR = 'var(--lighthouse-text-review)';

/**
 * Says the agent filled this field in, how surely, and offers to undo it.
 *
 * Both states carry the Ask mark, because both are the agent's work. Only `needsReview` takes the
 * caution colour: that is the one the user has to look at, and colouring the confident case too
 * would spend the warning on every filled field at once and leave the two indistinguishable.
 *
 * The wording carries the meaning on its own, so nothing here depends on the colour being seen.
 */
function ProvenanceMark({
  provenance,
  onRevert,
}: {
  provenance: FieldProvenance;
  onRevert?: () => void;
}) {
  const needsReview = provenance === 'needsReview';
  return (
    <View row className={[classNames.provenance, needsReview && classNames.provenanceReview]}>
      {/* Uncoloured on purpose: the asset strokes in `currentColor`, so it takes the colour
          `.provenance` sets and the icon and the words can never disagree. */}
      <Icon name={ASK_MARK_ICON} gridSize={2.5} className={classNames.provenanceMark} />
      {/* `color` rather than the class, because `Text` redeclares `--font-color` on itself and an
          inherited one never reaches it. `inactive` is that same variable by another name. */}
      <Text size="body2" inactive={!needsReview} color={needsReview ? REVIEW_COLOR : undefined}>
        {needsReview
          ? i18n('card.form.fieldHelp.bestGuess')
          : i18n('card.form.fieldHelp.autofilled')}
      </Text>
      {/* Beside the words rather than in the field itself: what it undoes is the mark, so it
          belongs with the mark and disappears with it. `ClearButton` is the field's own control and
          empties the value; this one restores whatever the proposal wrote over, which is often not
          empty at all. */}
      {!isNil(onRevert) && <RevertButton onRevert={onRevert} />}
    </View>
  );
}

/**
 * Puts the field back the way it was before the agent filled it in.
 *
 * `ghost` and sized down from `small`, because it sits inside a line of help text rather than in a
 * row of controls: a filled or outlined button of the usual size would read as the loudest thing on
 * a field it is only annotating.
 */
function RevertButton({ onRevert }: { onRevert: () => void }) {
  const label = i18n('card.form.fieldHelp.revert');
  return (
    <Button
      size="small"
      variant="ghost"
      icon="arrow-revert"
      iconOnly
      label={label}
      onClick={onRevert}
      className={classNames.revertButton}
      tooltip={label}
    />
  );
}

function EditFieldWrapper({
  children,
  onPress,
  hasError,
  isReadOnly,
  isGrayScale,
  isItalic,
  label,
}: Pick<
  EditFieldContainerProps,
  'children' | 'onPress' | 'isGrayScale' | 'isItalic' | 'isReadOnly' | 'label'
> & {
  hasError?: boolean;
}) {
  const isClickable = !isNil(onPress) && !isReadOnly;

  return (
    <View
      className={[
        classNames.wrapper,
        isReadOnly && classNames.wrapperReadOnly,
        hasError && classNames.wrapperError,
        isGrayScale && classNames.wrapperGrayScale,
        isItalic && classNames.wrapperItalic,
      ]}
      data-slot="edit-field-wrapper"
    >
      {/* Sibling overlay instead of a wrapping <button>: interactive children
          (e.g. ClearButton) would be invalid DOM inside a button element. */}
      {isClickable && (
        <button
          type="button"
          className={classNames.pressOverlay}
          onClick={onPress}
          aria-label={label}
        />
      )}
      {children}
    </View>
  );
}

interface PrintContentProps {
  label?: string | null;
  content?: string | null;
}

export function PrintContent({ label, content }: PrintContentProps) {
  const renderPrintContent = !isNil(content) && !isEmpty(content);

  if (!renderPrintContent) {
    return null;
  }

  return (
    <div className="print-only print-content-section">
      {!isNil(label) && (
        <div>
          <Text size="overline" weight="semibold">
            {label}
          </Text>
        </div>
      )}
      <div>
        <Text size="body1">{content}</Text>
      </div>
    </div>
  );
}
